import { Modal, notification, Popover, Switch, Tooltip } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import APIClient from '../api/api';
import {
  ListAutoBidsResponse,
  ListNCAftermarketBidsResponse,
  ListNCAftermarketSalesRequest,
  ListNCAftermarketSalesResponse,
  ListWatchlistResponse,
  NcAuctionBid,
  NcAuctionBidConfig,
  NcAuctionExcludedTld,
  NcAuctionWatchlistItem,
  NCAftermarketBid,
  NCAftermarketSale,
} from '../api/models';
import moment from 'moment';
import { formatDateTime } from '../utils';
import { debounce } from 'lodash';
import { Button, Column, DataTable, DomainName, FilterPill, Num, StatusTag, TabItem, Tabs } from '../components/ui';
import { PageFooter, PageHead, PageToolbar } from '../layout';

const DEFAULT_PAGE_SIZE = 25;

// Dropdown options for "ending within" — values are window sizes in seconds.
const ENDING_WITHIN_OPTIONS: { label: string; value: string }[] = [
  { label: 'Next 1 hour', value: '3600' },
  { label: 'Next 6 hours', value: '21600' },
  { label: 'Next 24 hours', value: '86400' },
  { label: 'Next 3 days', value: '259200' },
  { label: 'Next 7 days', value: '604800' },
  { label: 'Next 30 days', value: '2592000' },
  { label: 'Any time', value: '' },
];
const DEFAULT_ENDING_WITHIN = '604800'; // 7 days
const URGENT_THRESHOLD_MS = 60 * 60 * 1000; // < 1h = urgent row
const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

function endingWindowLabel(value: string): string {
  return ENDING_WITHIN_OPTIONS.find((o) => o.value === value)?.label.replace('Next ', '') ?? '—';
}

function buildEndDateRange(windowSeconds: string): string | undefined {
  if (!windowSeconds) return undefined;
  const window = parseInt(windowSeconds, 10);
  if (!Number.isFinite(window) || window <= 0) return undefined;
  const nowSec = Math.floor(Date.now() / 1000);
  return `${nowSec}_${nowSec + window}`;
}

// Convert "100-1000" → "100_1000".
function toRange(input?: string): string | undefined {
  if (!input) return undefined;
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  return trimmed.replace(/-/g, '_');
}

interface FilterState {
  endingWithin: string;
  name?: string;
  tld?: string;
  keywords?: string;
  price?: string;
  nameLength?: string;
  bidCount?: string;
  noHyphens?: boolean;
  noNumbers?: boolean;
}

const defaultFilters: FilterState = { endingWithin: DEFAULT_ENDING_WITHIN };

function buildRequest(f: FilterState): ListNCAftermarketSalesRequest {
  return {
    pageSize: DEFAULT_PAGE_SIZE,
    orderBy: 'end_time',
    direction: 'asc',
    endDate: buildEndDateRange(f.endingWithin),
    name: f.name || undefined,
    tld: f.tld || undefined,
    keywords: f.keywords || undefined,
    price: toRange(f.price),
    nameLength: toRange(f.nameLength),
    bidCount: toRange(f.bidCount),
    noHyphens: f.noHyphens || undefined,
    noNumbers: f.noNumbers || undefined,
  };
}

type TabKey = 'auctions' | 'bids' | 'watchlist' | 'autoBids' | 'excludedTlds' | 'config';

const VALID_TABS: TabKey[] = ['auctions', 'bids', 'watchlist', 'autoBids', 'excludedTlds', 'config'];

function getTabFromHash(): TabKey {
  const hash = window.location.hash.replace('#', '');
  return VALID_TABS.includes(hash as TabKey) ? (hash as TabKey) : 'auctions';
}

const NCAftermarket = () => {
  const [activeTab, setActiveTab] = useState<TabKey>(getTabFromHash);
  const [auctions, setAuctions] = useState<ListNCAftermarketSalesResponse>();
  const [auctionsLoading, setAuctionsLoading] = useState(false);
  const [bids, setBids] = useState<ListNCAftermarketBidsResponse>();
  const [bidsLoading, setBidsLoading] = useState(false);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [bidsPage, setBidsPage] = useState(1);
  const [bidsSearch, setBidsSearch] = useState('');
  const [bidsLeading, setBidsLeading] = useState('');

  // Watchlist state
  const [watchlist, setWatchlist] = useState<ListWatchlistResponse>();
  const [watchlistLoading, setWatchlistLoading] = useState(false);
  const [watchlistPage, setWatchlistPage] = useState(1);
  const [watchlistStatus, setWatchlistStatus] = useState<string>('');
  const [watchlistShowRemoved, setWatchlistShowRemoved] = useState(false);
  const [watchlistSearch, setWatchlistSearch] = useState('');
  const watchlistSearchRef = useRef<ReturnType<typeof setTimeout>>();

  // Auto Bids state
  const [autoBids, setAutoBids] = useState<ListAutoBidsResponse>();
  const [autoBidsLoading, setAutoBidsLoading] = useState(false);
  const [autoBidsPage, setAutoBidsPage] = useState(1);

  // Excluded TLDs state
  const [excludedTlds, setExcludedTlds] = useState<NcAuctionExcludedTld[]>([]);
  const [excludedTldsLoading, setExcludedTldsLoading] = useState(false);
  const [newTld, setNewTld] = useState('');

  // Bid Configuration state
  const [bidConfig, setBidConfig] = useState<NcAuctionBidConfig>();
  const [configLoading, setConfigLoading] = useState(false);
  const [configSaving, setConfigSaving] = useState(false);

  // Initial load.
  useEffect(() => {
    loadAuctions(buildRequest(filters));
    loadBids(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadAuctions(req: ListNCAftermarketSalesRequest) {
    setAuctionsLoading(true);
    try {
      setAuctions(await APIClient.listNCAftermarketSales(req));
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load auctions.' });
    } finally {
      setAuctionsLoading(false);
    }
  }

  async function loadBids(page: number) {
    setBidsLoading(true);
    try {
      const resp = await APIClient.listNCAftermarketBids({ page, pageSize: DEFAULT_PAGE_SIZE });
      setBids(resp);
      setBidsPage(resp.pages?.page || page);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load bids.' });
    } finally {
      setBidsLoading(false);
    }
  }

  const loadWatchlist = useCallback(async (page: number, opts?: { status?: string; statusNot?: string; domain?: string }) => {
    setWatchlistLoading(true);
    try {
      const resp = await APIClient.listWatchlist({
        page,
        pageSize: DEFAULT_PAGE_SIZE,
        status: opts?.status || undefined,
        statusNot: opts?.statusNot || undefined,
        domain: opts?.domain || undefined,
      });
      setWatchlist(resp);
      setWatchlistPage(resp.currentPage || page);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load watchlist.' });
    } finally {
      setWatchlistLoading(false);
    }
  }, []);

  const loadAutoBids = useCallback(async (page: number) => {
    setAutoBidsLoading(true);
    try {
      const resp = await APIClient.listAutoBids({ page, pageSize: DEFAULT_PAGE_SIZE });
      setAutoBids(resp);
      setAutoBidsPage(resp.currentPage || page);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load auto bids.' });
    } finally {
      setAutoBidsLoading(false);
    }
  }, []);

  const loadExcludedTlds = useCallback(async () => {
    setExcludedTldsLoading(true);
    try {
      setExcludedTlds(await APIClient.listExcludedTlds());
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load excluded TLDs.' });
    } finally {
      setExcludedTldsLoading(false);
    }
  }, []);

  const loadBidConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      setBidConfig(await APIClient.getAutoBidderConfig());
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to load bid config.' });
    } finally {
      setConfigLoading(false);
    }
  }, []);

  function watchlistOpts() {
    return {
      status: watchlistStatus || undefined,
      statusNot: !watchlistStatus && !watchlistShowRemoved ? 'removed' : undefined,
      domain: watchlistSearch || undefined,
    };
  }

  function reloadWatchlist(page = 1) {
    loadWatchlist(page, watchlistOpts());
  }

  async function handleRemoveWatchlistItem(id: number) {
    try {
      await APIClient.removeWatchlistItem(id);
      reloadWatchlist(watchlistPage);
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to remove domain.' });
    }
  }

  // Load data when switching to a new tab
  useEffect(() => {
    if (activeTab === 'watchlist' && !watchlist) reloadWatchlist();
    if (activeTab === 'autoBids' && !autoBids) loadAutoBids(1);
    if (activeTab === 'excludedTlds' && excludedTlds.length === 0) loadExcludedTlds();
    if (activeTab === 'config' && !bidConfig) loadBidConfig();
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleFileUpload(type: 'watchlist' | 'watchlistRemove' | 'excludedTlds') {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt,.csv';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      try {
        if (type === 'watchlist') {
          const resp = await APIClient.uploadWatchlist(file);
          notification.success({ message: `Uploaded: ${resp.accepted.length} accepted, ${resp.rejected.length} rejected` });
          if (resp.rejected.length > 0) {
            notification.warning({
              message: 'Rejected domains',
              description: resp.rejected.slice(0, 10).map(r => `${r.domain}: ${r.reason}`).join('\n'),
              duration: 10,
            });
          }
          reloadWatchlist();
        } else if (type === 'watchlistRemove') {
          const resp = await APIClient.removeWatchlist(file);
          notification.success({ message: `Removed: ${resp.removed.length}, not found: ${resp.notFound.length}` });
          reloadWatchlist();
        } else if (type === 'excludedTlds') {
          const resp = await APIClient.uploadExcludedTlds(file);
          notification.success({ message: `Added: ${resp.added.length}, skipped: ${resp.skipped.length}` });
          loadExcludedTlds();
        }
      } catch (e) {
        console.error(e);
        notification.error({ message: 'Upload failed.' });
      }
    };
    input.click();
  }

  async function handleAddTld() {
    if (!newTld.trim()) return;
    try {
      await APIClient.addExcludedTld(newTld.trim());
      setNewTld('');
      loadExcludedTlds();
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to add TLD.' });
    }
  }

  async function handleDeleteTld(id: number) {
    try {
      await APIClient.deleteExcludedTld(id);
      loadExcludedTlds();
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to delete TLD.' });
    }
  }

  async function updateConfig(patch: Record<string, any>) {
    setConfigSaving(true);
    try {
      setBidConfig(await APIClient.updateAutoBidderConfig(patch));
      notification.success({ message: 'Config updated.' });
    } catch (e) {
      console.error(e);
      notification.error({ message: 'Failed to update config.' });
    } finally {
      setConfigSaving(false);
    }
  }

  // Debounce text input changes; fire selects/switches immediately.
  const reload = useMemo(
    () => debounce((next: FilterState) => loadAuctions(buildRequest(next)), 350),
    [],
  );

  function updateFilter<K extends keyof FilterState>(key: K, value: FilterState[K], immediate = false) {
    const next = { ...filters, [key]: value };
    setFilters(next);
    setCursorStack([]);
    if (immediate) {
      reload.cancel();
      loadAuctions(buildRequest(next));
    } else {
      reload(next);
    }
  }

  function clearFilters() {
    setFilters(defaultFilters);
    setCursorStack([]);
    loadAuctions(buildRequest(defaultFilters));
  }

  function nextPage() {
    if (!auctions?.nextCursor) return;
    const newStack = [...cursorStack, auctions.nextCursor];
    setCursorStack(newStack);
    loadAuctions({ ...buildRequest(filters), cursor: auctions.nextCursor });
  }

  function prevPage() {
    if (cursorStack.length === 0) return;
    const newStack = cursorStack.slice(0, -1);
    setCursorStack(newStack);
    const cursor = newStack.length > 0 ? newStack[newStack.length - 1] : undefined;
    loadAuctions({ ...buildRequest(filters), cursor });
  }

  const filteredBids = useMemo(() => {
    let items = bids?.items ?? [];
    if (bidsSearch.trim()) {
      const q = bidsSearch.trim().toLowerCase();
      items = items.filter((b) => b.name?.toLowerCase().includes(q));
    }
    if (bidsLeading === 'leading') items = items.filter((b) => b.isLeadingBid);
    else if (bidsLeading === 'outbid') items = items.filter((b) => !b.isLeadingBid);
    return items;
  }, [bids, bidsSearch, bidsLeading]);

  const activeFilterCount =
    [filters.name, filters.tld, filters.keywords, filters.price, filters.nameLength, filters.bidCount]
      .filter((v) => v && v.toString().trim().length > 0).length +
    (filters.noHyphens ? 1 : 0) +
    (filters.noNumbers ? 1 : 0);

  const tabs: TabItem[] = [
    { key: 'auctions', label: 'Live', count: auctions?.items.length },
    { key: 'bids', label: 'My Bids', count: bids?.pages?.total ?? bids?.items.length },
    { key: 'watchlist', label: 'Watchlist', count: watchlist?.total ?? undefined },
    { key: 'autoBids', label: 'Auto Bids', count: autoBids?.total ?? undefined },
    { key: 'excludedTlds', label: 'Excluded TLDs', count: excludedTlds.length || undefined },
    { key: 'config', label: 'Bid Configuration' },
  ];

  return (
    <>
      <PageHead
        title="NC Aftermarket"
        subtitle="Live aftermarket inventory across Namecheap auctions and buy-now listings."
        /* TODO: add Export and + New bid action buttons */
      />

      <PageToolbar>
        <Tabs items={tabs} activeKey={activeTab} onChange={(k) => { setActiveTab(k as TabKey); window.location.hash = k; }} separator />
        {activeTab === 'auctions' && (
          <>
            <FilterPopover
              label="Ending"
              value={endingWindowLabel(filters.endingWithin)}
              active={filters.endingWithin !== DEFAULT_ENDING_WITHIN}
              content={(close) => (
                <div className="flex flex-col min-w-[160px]">
                  {ENDING_WITHIN_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        updateFilter('endingWithin', opt.value, true);
                        close();
                      }}
                      className={`text-left px-2 py-1.5 text-[12.5px] rounded hover:bg-bg-elevated ${
                        filters.endingWithin === opt.value ? 'text-accent font-semibold' : 'text-text-2'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            />
            <FilterTextPopover
              label="Name"
              value={filters.name}
              placeholder="example"
              onChange={(v) => updateFilter('name', v)}
            />
            <FilterTextPopover
              label="TLD"
              value={filters.tld}
              placeholder="com,io"
              onChange={(v) => updateFilter('tld', v)}
            />
            <FilterTextPopover
              label="Price"
              value={filters.price}
              placeholder="100-5000"
              onChange={(v) => updateFilter('price', v)}
            />
            <FilterTextPopover
              label="Length"
              value={filters.nameLength}
              placeholder="3-6"
              onChange={(v) => updateFilter('nameLength', v)}
            />
            <FilterTextPopover
              label="Bids"
              value={filters.bidCount}
              placeholder="1-"
              onChange={(v) => updateFilter('bidCount', v)}
            />
            {activeFilterCount > 0 && (
              <button
                type="button"
                onClick={clearFilters}
                className="text-[11px] text-text-3 hover:text-text font-mono px-1"
              >
                clear ({activeFilterCount})
              </button>
            )}
          </>
        )}
      </PageToolbar>

      {activeTab === 'auctions' && (
        <>
          <DataTable
            columns={auctionColumns}
            data={auctions?.items ?? []}
            rowKey={(s, i) => s.id || s.name || String(i)}
            loading={auctionsLoading}
            rowClassName={(s) => isUrgent(s) ? 'bg-danger-soft/30 hover:bg-danger-soft/50' : ''}
            emptyMessage="No live auctions match these filters."
          />
          <PageFooter
            left={
              <span>
                Showing <b className="text-text-2">{auctions?.items.length ?? 0}</b> · refresh <b className="text-text-2">manual</b> ·
                order <b className="text-text-2">end_time asc</b>
              </span>
            }
            right={
              <div className="flex items-center gap-3">
                <button type="button" onClick={prevPage} disabled={cursorStack.length === 0 || auctionsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed">← prev</button>
                <button type="button" onClick={nextPage} disabled={!auctions?.hasMore || auctionsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed">next →</button>
              </div>
            }
          />
        </>
      )}

      {activeTab === 'bids' && (
        <>
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border flex-wrap">
            <input
              type="text"
              value={bidsSearch}
              onChange={(e) => setBidsSearch(e.target.value)}
              placeholder="Search domains..."
              className="px-2 py-1.5 bg-panel border border-border-strong rounded-md text-[12.5px] text-text outline-none focus:border-accent w-[200px]"
            />
            <select
              value={bidsLeading}
              onChange={(e) => setBidsLeading(e.target.value)}
              className="px-2 py-1.5 bg-panel border border-border-strong rounded-md text-[12.5px] text-text outline-none focus:border-accent"
            >
              <option value="">All bids</option>
              <option value="leading">Leading only</option>
              <option value="outbid">Outbid only</option>
            </select>
          </div>
          <DataTable
            columns={bidColumns}
            data={filteredBids}
            rowKey={(b, i) => b.id || String(i)}
            loading={bidsLoading}
            emptyMessage="No bids placed yet."
          />
          <PageFooter
            left={
              <span>
                Page <b className="text-text-2">{bidsPage}</b> of <b className="text-text-2">{bids?.pages?.lastPage ?? 1}</b> ·
                total <b className="text-text-2">{bids?.pages?.total ?? bids?.items.length ?? 0}</b>
              </span>
            }
            right={
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => loadBids(Math.max(1, bidsPage - 1))}
                  disabled={bidsPage <= 1 || bidsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed">← prev</button>
                <button type="button" onClick={() => loadBids(bidsPage + 1)}
                  disabled={(bids?.pages?.lastPage ?? 1) <= bidsPage || bidsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed">next →</button>
              </div>
            }
          />
        </>
      )}

      {activeTab === 'watchlist' && (
        <>
          <div className="flex items-center gap-2 px-4 py-2 border-b border-border flex-wrap">
            <input
              type="text"
              value={watchlistSearch}
              onChange={(e) => {
                setWatchlistSearch(e.target.value);
                clearTimeout(watchlistSearchRef.current);
                const val = e.target.value;
                watchlistSearchRef.current = setTimeout(() => {
                  loadWatchlist(1, {
                    status: watchlistStatus || undefined,
                    statusNot: !watchlistStatus && !watchlistShowRemoved ? 'removed' : undefined,
                    domain: val || undefined,
                  });
                }, 350);
              }}
              placeholder="Search domains..."
              className="px-2 py-1.5 bg-panel border border-border-strong rounded-md text-[12.5px] text-text outline-none focus:border-accent w-[200px]"
            />
            <select
              value={watchlistStatus}
              onChange={(e) => {
                setWatchlistStatus(e.target.value);
                loadWatchlist(1, {
                  status: e.target.value || undefined,
                  statusNot: !e.target.value && !watchlistShowRemoved ? 'removed' : undefined,
                  domain: watchlistSearch || undefined,
                });
              }}
              className="px-2 py-1.5 bg-panel border border-border-strong rounded-md text-[12.5px] text-text outline-none focus:border-accent"
            >
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="active">Active</option>
              <option value="auction_found">Auction Found</option>
              <option value="awaiting_closeout">Awaiting Closeout</option>
              <option value="closeout_missed">Closeout Missed</option>
              <option value="bid_placed">Bid Placed</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
              <option value="rejected">Rejected</option>
              <option value="removed">Removed</option>
            </select>
            <label className="flex items-center gap-1.5 text-[12.5px] text-text-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={watchlistShowRemoved}
                onChange={(e) => {
                  setWatchlistShowRemoved(e.target.checked);
                  loadWatchlist(1, {
                    status: watchlistStatus || undefined,
                    statusNot: !watchlistStatus && !e.target.checked ? 'removed' : undefined,
                    domain: watchlistSearch || undefined,
                  });
                }}
                className="accent-accent"
              />
              Show removed
            </label>
            <div className="flex-1" />
            <Button size="sm" onClick={() => handleFileUpload('watchlist')}>Upload Domains</Button>
            <Button size="sm" variant="ghost" onClick={() => handleFileUpload('watchlistRemove')}>Bulk Remove</Button>
          </div>
          <DataTable
            columns={getWatchlistColumns(handleRemoveWatchlistItem)}
            data={watchlist?.content ?? []}
            rowKey={(w) => String(w.id)}
            loading={watchlistLoading}
            emptyMessage="No domains in watchlist. Upload a file to get started."
          />
          <PageFooter
            left={<span>Page <b className="text-text-2">{watchlistPage}</b> of <b className="text-text-2">{watchlist?.totalPages ?? 1}</b> · total <b className="text-text-2">{watchlist?.total ?? 0}</b></span>}
            right={
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => reloadWatchlist(Math.max(1, watchlistPage - 1))}
                  disabled={watchlistPage <= 1 || watchlistLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed">← prev</button>
                <button type="button" onClick={() => reloadWatchlist(watchlistPage + 1)}
                  disabled={(watchlist?.totalPages ?? 1) <= watchlistPage || watchlistLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed">next →</button>
              </div>
            }
          />
        </>
      )}

      {activeTab === 'autoBids' && (
        <>
          {bidConfig && !bidConfig.liveMode && (
            <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20 text-[12.5px] text-amber-600">
              Dry-Run Mode — bids are simulated, no real bids are placed.
            </div>
          )}
          <DataTable
            columns={autoBidColumns}
            data={autoBids?.content ?? []}
            rowKey={(b) => String(b.id)}
            loading={autoBidsLoading}
            rowClassName={(b) => b.dryRun ? 'opacity-60' : ''}
            emptyMessage="No auto bids recorded yet."
          />
          <PageFooter
            left={<span>Page <b className="text-text-2">{autoBidsPage}</b> of <b className="text-text-2">{autoBids?.totalPages ?? 1}</b> · total <b className="text-text-2">{autoBids?.total ?? 0}</b></span>}
            right={
              <div className="flex items-center gap-3">
                <button type="button" onClick={() => loadAutoBids(Math.max(1, autoBidsPage - 1))}
                  disabled={autoBidsPage <= 1 || autoBidsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed">← prev</button>
                <button type="button" onClick={() => loadAutoBids(autoBidsPage + 1)}
                  disabled={(autoBids?.totalPages ?? 1) <= autoBidsPage || autoBidsLoading}
                  className="hover:text-text disabled:opacity-30 disabled:cursor-not-allowed">next →</button>
              </div>
            }
          />
        </>
      )}

      {activeTab === 'excludedTlds' && (
        <div className="p-4">
          <div className="flex items-center gap-2 mb-4">
            <input
              type="text"
              value={newTld}
              onChange={(e) => setNewTld(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAddTld()}
              placeholder="e.g. com.br"
              className="px-2 py-1.5 bg-panel border border-border-strong rounded-md text-[12.5px] text-text outline-none focus:border-accent w-[180px]"
            />
            <Button size="sm" onClick={handleAddTld}>Add TLD</Button>
            <Button size="sm" variant="ghost" onClick={() => handleFileUpload('excludedTlds')}>Upload File</Button>
          </div>
          {excludedTldsLoading ? (
            <div className="text-text-3 text-[12.5px]">Loading...</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {excludedTlds.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-bg-elevated rounded-md text-[12.5px] text-text-2 border border-border">
                  .{t.tld}
                  <button type="button" onClick={() => handleDeleteTld(t.id)}
                    className="text-text-3 hover:text-danger text-[10px] leading-none">&times;</button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'config' && bidConfig && (
        <BidConfigPanel config={bidConfig} saving={configSaving} onUpdate={updateConfig} />
      )}
      {activeTab === 'config' && configLoading && (
        <div className="p-4 text-text-3 text-[12.5px]">Loading configuration...</div>
      )}
    </>
  );
};

function isUrgent(sale: NCAftermarketSale): boolean {
  if (!sale.endDate) return false;
  const ms = sale.endDate.getTime() - Date.now();
  return ms > 0 && ms < URGENT_THRESHOLD_MS;
}

function formatRemaining(end: Date): string {
  const ms = end.getTime() - Date.now();
  if (ms <= 0) return 'ended';
  const totalSec = Math.floor(ms / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  if (days > 0) return `${days}d ${hours.toString().padStart(2, '0')}h`;
  if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  const seconds = Math.floor(totalSec % 60);
  return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
}

const auctionColumns: Column<NCAftermarketSale>[] = [
  {
    key: 'name',
    title: 'Domain',
    render: (s) => <DomainName name={s.name} />,
  },
  {
    key: 'status',
    title: 'Status',
    render: (s) => {
      if (!s.status) return <span className="text-text-3">—</span>;
      const lower = s.status.toLowerCase();
      const tone =
        lower.includes('live') || lower.includes('auction') ? 'success' :
        lower.includes('buy') || lower.includes('fixed') ? 'warn' :
        'neutral';
      return <StatusTag tone={tone}>{s.status}</StatusTag>;
    },
  },
  {
    key: 'type',
    title: 'Type',
    render: (s) => <span className="text-text-3 text-[12.5px]">{s.auctionType || s.saleType || '—'}</span>,
  },
  {
    key: 'price',
    title: 'Price',
    align: 'right',
    render: (s) => <Num value={s.price ?? null} format="currency" />,
  },
  {
    key: 'minBid',
    title: 'Min Bid',
    align: 'right',
    render: (s) => <Num value={s.minBid ?? null} format="currency" tone="muted" />,
  },
  {
    key: 'bidCount',
    title: 'Bids',
    align: 'right',
    render: (s) => <Num value={s.bidCount ?? 0} format="int" tone={s.bidCount ? 'default' : 'muted'} />,
  },
  {
    key: 'estibot',
    title: 'Estibot',
    align: 'right',
    render: (s) => <Num value={s.estibotValue ?? null} format="currency" />,
  },
  {
    key: 'ratio',
    title: 'Ratio',
    align: 'right',
    render: (s) => {
      if (s.estibotValue == null || s.price == null || s.price === 0) return <Num value={null} />;
      return <Num value={s.estibotValue / s.price} format="ratio" tone="up" />;
    },
  },
  {
    key: 'ends',
    title: 'Ends',
    align: 'right',
    render: (s) => {
      if (!s.endDate) return <Num value={null} />;
      const urgent = isUrgent(s);
      const ms = s.endDate.getTime() - Date.now();
      const showCountdown = ms <= SIX_HOURS_MS;
      return (
        <span
          title={moment(s.endDate).format('LLL')}
          className={`font-mono text-[12.5px] tabular-nums ${urgent ? 'text-danger font-semibold' : 'text-text'}`}
        >
          {showCountdown ? formatRemaining(s.endDate) : moment(s.endDate).format('MMM D h:mma')}
        </span>
      );
    },
  },
];

const bidColumns: Column<NCAftermarketBid>[] = [
  {
    key: 'name',
    title: 'Domain',
    render: (b) => <DomainName name={b.name} />,
  },
  {
    key: 'amount',
    title: 'Bid',
    align: 'right',
    render: (b) => <Num value={b.amount ?? null} format="currency" />,
  },
  {
    key: 'maxAmount',
    title: 'Max',
    align: 'right',
    render: (b) => <Num value={b.maxAmount ?? null} format="currency" tone="muted" />,
  },
  {
    key: 'isProxy',
    title: 'Proxy',
    render: (b) => (
      <span className="text-text-3 text-[12.5px]">{b.isProxy ? 'Yes' : 'No'}</span>
    ),
  },
  {
    key: 'isLeading',
    title: 'Leading',
    render: (b) =>
      b.isLeadingBid ? <StatusTag tone="success">Leading</StatusTag> : <StatusTag tone="neutral">Outbid</StatusTag>,
  },
  {
    key: 'status',
    title: 'Status',
    render: (b) => <span className="text-text-3 text-[12.5px]">{b.status || '—'}</span>,
  },
  {
    key: 'created',
    title: 'Placed',
    align: 'right',
    render: (b) => (
      <span className="font-mono text-[12.5px] text-text-3 tabular-nums">
        {b.createdDate ? formatDateTime(b.createdDate) : '—'}
      </span>
    ),
  },
];

function getWatchlistColumns(onRemove: (id: number) => void): Column<NcAuctionWatchlistItem>[] {
  return [
    {
      key: 'domain',
      title: 'Domain',
      render: (w) => <DomainName name={w.domain} />,
    },
    {
      key: 'tld',
      title: 'TLD',
      render: (w) => <span className="text-text-3 text-[12.5px]">.{w.tld}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      render: (w) => {
        const tone =
          w.status === 'won' ? 'success' :
          w.status === 'auction_found' || w.status === 'bid_placed' || w.status === 'awaiting_closeout' ? 'warn' :
          w.status === 'rejected' || w.status === 'lost' || w.status === 'closeout_missed' ? 'danger' :
          'neutral';
        return (
          <span title={w.rejectionReason || undefined} className="inline-flex flex-col gap-0.5">
            <StatusTag tone={tone}>{w.status.replace(/_/g, ' ')}</StatusTag>
            {w.rejectionReason && (
              <span className="text-[10.5px] text-text-3 max-w-[260px] truncate">{w.rejectionReason}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'saleId',
      title: 'Sale ID',
      render: (w) => <span className="font-mono text-[12.5px] text-text-3">{w.saleId || '—'}</span>,
    },
    {
      key: 'minBid',
      title: 'Min Bid',
      align: 'right',
      render: (w) => <Num value={w.minBid ?? null} format="currency" tone="muted" />,
    },
    {
      key: 'currentPrice',
      title: 'Price',
      align: 'right',
      render: (w) => <Num value={w.currentPrice ?? null} format="currency" />,
    },
    {
      key: 'auctionEndDate',
      title: 'Ends',
      align: 'right',
      render: (w) => (
        <span className="font-mono text-[12.5px] text-text-3 tabular-nums">
          {w.auctionEndDate ? formatDateTime(w.auctionEndDate) : '—'}
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Added',
      align: 'right',
      render: (w) => (
        <span className="font-mono text-[12.5px] text-text-3 tabular-nums">
          {formatDateTime(w.createdAt)}
        </span>
      ),
    },
    {
      key: 'actions',
      title: '',
      width: 40,
      render: (w) => w.status !== 'removed' ? (
        <button
          type="button"
          onClick={() => onRemove(w.id)}
          className="text-text-3 hover:text-danger text-[11px]"
          title="Remove from watchlist"
        >
          &times;
        </button>
      ) : null,
    },
  ];
}

const autoBidColumns: Column<NcAuctionBid>[] = [
  {
    key: 'domain',
    title: 'Domain',
    render: (b) => <DomainName name={b.domain} />,
  },
  {
    key: 'saleId',
    title: 'Sale ID',
    render: (b) => <span className="font-mono text-[12.5px] text-text-3">{b.saleId}</span>,
  },
  {
    key: 'maxAmount',
    title: 'Max Bid',
    align: 'right',
    render: (b) => <Num value={b.maxAmount} format="currency" />,
  },
  {
    key: 'actualAmount',
    title: 'Actual',
    align: 'right',
    render: (b) => <Num value={b.actualAmount ?? null} format="currency" tone="muted" />,
  },
  {
    key: 'status',
    title: 'Status',
    render: (b) => {
      const tone =
        b.status === 'won' ? 'success' :
        b.status === 'placed' ? 'warn' :
        b.status === 'outbid' || b.status === 'failed' ? 'danger' :
        b.status === 'dry_run' ? 'neutral' :
        'neutral';
      return (
        <span className="flex items-center gap-1.5">
          <StatusTag tone={tone}>{b.status.replace(/_/g, ' ')}</StatusTag>
          {b.dryRun && <span className="text-[10px] text-amber-500 font-mono">DRY</span>}
        </span>
      );
    },
  },
  {
    key: 'latency',
    title: 'Latency',
    align: 'right',
    render: (b) => (
      <span className="font-mono text-[12.5px] text-text-3 tabular-nums">
        {b.bidLatencyMs != null ? `${b.bidLatencyMs}ms` : '—'}
      </span>
    ),
  },
  {
    key: 'createdAt',
    title: 'Placed',
    align: 'right',
    render: (b) => (
      <span className="font-mono text-[12.5px] text-text-3 tabular-nums">
        {formatDateTime(b.createdAt)}
      </span>
    ),
  },
];

interface BidConfigPanelProps {
  config: NcAuctionBidConfig;
  saving: boolean;
  onUpdate: (patch: Record<string, any>) => void;
}

function BidConfigPanel({ config, saving, onUpdate }: BidConfigPanelProps) {
  const [liveModeModalOpen, setLiveModeModalOpen] = useState(false);

  function ConfigLabel({ label, tip }: { label: string; tip: string }) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-[12.5px] text-text-2 font-medium">{label}</span>
        <Tooltip title={tip}>
          <span className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full bg-bg-elevated border border-border text-[9px] text-text-3 cursor-help">i</span>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-lg space-y-5">
      <div className="flex items-center justify-between">
        <ConfigLabel label="Enabled" tip="Master switch for the auto-bidder. When disabled, no scans or bids will run." />
        <Switch checked={config.enabled} loading={saving} onChange={(v) => onUpdate({ enabled: v })} />
      </div>

      <div className="flex items-center justify-between">
        <ConfigLabel label="Live Mode" tip="When ON, real bids are placed via the NC API. When OFF, bids are simulated (dry-run). Defaults to OFF for safety." />
        <Switch
          checked={config.liveMode}
          loading={saving}
          onChange={(v) => {
            if (v) {
              setLiveModeModalOpen(true);
            } else {
              onUpdate({ liveMode: false });
            }
          }}
        />
      </div>

      <Modal
        title="Enable Live Mode?"
        open={liveModeModalOpen}
        onCancel={() => setLiveModeModalOpen(false)}
        onOk={() => {
          onUpdate({ liveMode: true });
          setLiveModeModalOpen(false);
        }}
        okText="Enable Live Bidding"
        okButtonProps={{ danger: true }}
      >
        <p className="text-[13px] text-text-2">
          This will enable <strong>real bid placement</strong> on Namecheap auctions. Real money will be spent. Are you sure?
        </p>
      </Modal>

      <div className="flex items-center justify-between">
        <ConfigLabel label="Max Bid Amount ($)" tip="Maximum dollar amount the auto-bidder will bid on any single auction." />
        <input
          type="number"
          defaultValue={config.maxBidAmount}
          onBlur={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v) && v !== config.maxBidAmount) onUpdate({ maxBidAmount: v });
          }}
          className="w-24 px-2 py-1 bg-panel border border-border-strong rounded text-[12.5px] text-text text-right outline-none focus:border-accent"
        />
      </div>

      <div className="flex items-center justify-between">
        <ConfigLabel label="Min Bid Reject Threshold ($)" tip="Auctions with a minimum bid above this threshold will be automatically rejected from the watchlist." />
        <input
          type="number"
          defaultValue={config.minBidRejectThreshold}
          onBlur={(e) => {
            const v = parseFloat(e.target.value);
            if (!isNaN(v) && v !== config.minBidRejectThreshold) onUpdate({ minBidRejectThreshold: v });
          }}
          className="w-24 px-2 py-1 bg-panel border border-border-strong rounded text-[12.5px] text-text text-right outline-none focus:border-accent"
        />
      </div>

      <div className="flex items-center justify-between">
        <ConfigLabel label="Bid Window (minutes)" tip="How many minutes before auction end the bidder is allowed to place bids. Bids are only placed within this window." />
        <input
          type="number"
          defaultValue={config.bidWindowMinutes}
          onBlur={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v) && v !== config.bidWindowMinutes) onUpdate({ bidWindowMinutes: v });
          }}
          className="w-24 px-2 py-1 bg-panel border border-border-strong rounded text-[12.5px] text-text text-right outline-none focus:border-accent"
        />
      </div>

      <div className="flex items-center justify-between">
        <ConfigLabel label="Bid Floor (minutes)" tip="Minimum minutes before auction end to place a bid. Prevents bidding too close to the deadline where latency could cause issues." />
        <input
          type="number"
          defaultValue={config.bidFloorMinutes}
          onBlur={(e) => {
            const v = parseInt(e.target.value, 10);
            if (!isNaN(v) && v !== config.bidFloorMinutes) onUpdate({ bidFloorMinutes: v });
          }}
          className="w-24 px-2 py-1 bg-panel border border-border-strong rounded text-[12.5px] text-text text-right outline-none focus:border-accent"
        />
      </div>

      <div className="flex items-center justify-between opacity-50">
        <ConfigLabel label="Daily Budget Cap ($)" tip="Maximum total spend per day across all auto-bids. Coming soon." />
        <input
          type="number"
          disabled
          value={config.dailyBudgetCap ?? 0}
          className="w-24 px-2 py-1 bg-panel border border-border-strong rounded text-[12.5px] text-text-3 text-right outline-none cursor-not-allowed"
        />
      </div>

      <div className="pt-3 border-t border-border text-[11px] text-text-3">
        Last updated: {formatDateTime(config.updatedAt)}
        {config.updatedBy && <> by <span className="text-text-2">{config.updatedBy}</span></>}
      </div>
    </div>
  );
}

interface FilterPopoverProps {
  label: string;
  value?: string;
  active?: boolean;
  content: (close: () => void) => React.ReactNode;
}

function FilterPopover({ label, value, active, content }: FilterPopoverProps) {
  const [open, setOpen] = useState(false);
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={<div className="p-1">{content(() => setOpen(false))}</div>}
    >
      <span>
        <FilterPill label={label} value={value || '—'} active={active} />
      </span>
    </Popover>
  );
}

interface FilterTextPopoverProps {
  label: string;
  value?: string;
  placeholder?: string;
  onChange: (v: string) => void;
}

function FilterTextPopover({ label, value, placeholder, onChange }: FilterTextPopoverProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value ?? '');

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger="click"
      placement="bottomLeft"
      content={
        <div className="p-1 min-w-[180px]">
          <input
            type="text"
            value={draft}
            placeholder={placeholder}
            autoFocus
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                onChange(draft);
                setOpen(false);
              }
              if (e.key === 'Escape') setOpen(false);
            }}
            className="w-full px-2 py-1.5 bg-panel border border-border-strong rounded-md text-[12.5px] text-text outline-none focus:border-accent"
          />
          <div className="flex justify-end gap-1.5 mt-2">
            <button
              type="button"
              onClick={() => {
                setDraft('');
                onChange('');
                setOpen(false);
              }}
              className="text-[11px] text-text-3 hover:text-text px-2 py-1"
            >
              clear
            </button>
            <button
              type="button"
              onClick={() => {
                onChange(draft);
                setOpen(false);
              }}
              className="text-[11px] text-accent font-semibold hover:opacity-80 px-2 py-1"
            >
              apply
            </button>
          </div>
        </div>
      }
    >
      <span>
        <FilterPill label={label} value={value || '—'} active={!!value} />
      </span>
    </Popover>
  );
}

export default NCAftermarket;
